"use client";

import { useOrganizationWallets } from "@/hooks/useOrganizationWallets";
import { B3OS_ORG_COOKIE_NAME } from "@/lib/authenticatedFetch";
import type { OrganizationWallet } from "@/types/wallet";
import Cookies from "js-cookie";
import { createContext, useContext, useMemo, type ReactNode } from "react";

interface OrgWalletsContextValue {
  wallets: OrganizationWallet[];
  isLoading: boolean;
}

const OrgWalletsContext = createContext<OrgWalletsContextValue>({
  wallets: [],
  isLoading: true,
});

/**
 * Provides organization wallets to all canvas children via context.
 * This prevents N ActionNodes from each creating independent TanStack Query
 * subscriptions to the same ["organization-wallets", orgId] key.
 */
export function OrganizationWalletsProvider({ children }: { children: ReactNode }) {
  const organizationId = Cookies.get(B3OS_ORG_COOKIE_NAME);
  const { wallets, isLoading } = useOrganizationWallets(organizationId);
  const value = useMemo(() => ({ wallets, isLoading }), [wallets, isLoading]);
  return <OrgWalletsContext.Provider value={value}>{children}</OrgWalletsContext.Provider>;
}

/**
 * Read organization wallets from the canvas-level context.
 * Must be used inside OrganizationWalletsProvider.
 */
export function useOrgWalletsContext() {
  return useContext(OrgWalletsContext);
}
